// src/pages/dashboard/branch/menu-items/components/DeleteMenuItemModal.tsx
import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import Button from '../../../../../components/ui/Button';
import { MenuItem } from '../../../../../utils/supabaseClient';

import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { deleteMenuItemData } from '../../../../../store/slices/menuItemSlice';
import { selectCurrentBranch } from '../../../../../store/selectors';

interface DeleteMenuItemModalProps {
	isOpen: boolean;
	menuItem: MenuItem;
	onClose: () => void;
	onMenuItemDeleted: (deletedItem?: MenuItem) => void;
}

const DeleteMenuItemModal: React.FC<DeleteMenuItemModalProps> = ({
	isOpen,
	menuItem,
	onClose,
	onMenuItemDeleted
}) => {
	const { t } = useTranslation();
	const [isDeleting, setIsDeleting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const dispatch = useAppDispatch()

	const branch = useAppSelector(selectCurrentBranch);

	const handleConfirm = async () => {
		setIsDeleting(true);
		setError(null);

		try {
			// const result = await deleteMenuItem(menuItem.id);
			// if (!result) return;
			// if (!result.success) console.log(result.message);

			// onMenuItemDeleted(result.data);

			await dispatch(deleteMenuItemData(menuItem.id))
			onMenuItemDeleted();
			onClose();
		} catch (err) {
			console.error('Error deleting menu item:', err);
			setError(t('menuItems.errors.deleteFailed'));
		} finally {
			setIsDeleting(false);
		}
	};

	const handleClose = () => {
		if (!isDeleting) {
			setError(null);
			onClose();
		}
	};

	const modalVariants = {
		hidden: { opacity: 0, scale: 0.9 },
		visible: {
			opacity: 1,
			scale: 1,
			transition: { duration: 0.2 }
		},
		exit: {
			opacity: 0,
			scale: 0.9,
			transition: { duration: 0.15 }
		}
	};

	const overlayVariants = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 },
		exit: { opacity: 0 }
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
						<motion.div
							variants={overlayVariants}
							initial="hidden"
							animate="visible"
							exit="exit"
							className="fixed inset-0 bg-black/50"
							onClick={handleClose}
						/>

						<motion.div
							variants={modalVariants}
							initial="hidden"
							animate="visible"
							exit="exit"
							className="z-10 inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-start overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
										<FiAlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
									</div>
									<div className="mt-3 text-center sm:mt-0 sm:ms-4 sm:text-start">
										<h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
											{t('menuItems.deleteTitle')}
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
												{t('menuItems.deleteMessage', { name: menuItem.name })}
											</p>

											{/* Menu Item Preview */}
											<div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex items-center gap-3">
												{menuItem.image_url ? (
													<img
														src={menuItem.image_url}
														alt={menuItem.name}
														className="w-12 h-12 rounded-md object-cover flex-shrink-0"
													/>
												) : (
													branch?.default_item_image ? (
														<img
															src={branch?.default_item_image}
															alt={menuItem.name}
															className="w-12 h-12 rounded-md object-cover flex-shrink-0"
														/>
													) : (
														<div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded-md flex items-center justify-center flex-shrink-0">
															<span className="text-gray-500 dark:text-gray-400 text-sm font-medium">
																{menuItem.name?.charAt(0)}
															</span>
														</div>
													)
												)}
												<div className="flex-1 min-w-0">
													<h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
														{menuItem.name}
													</h4>
													{menuItem.price && (
														<p className="text-sm text-gray-500 dark:text-gray-400">
															${menuItem.price.toFixed(2)}
														</p>
													)}
												</div>
											</div>

											{error && (
												<motion.div
													initial={{ opacity: 0, y: -10 }}
													animate={{ opacity: 1, y: 0 }}
													className="mt-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm"
												>
													{error}
												</motion.div>
											)}
										</div>
									</div>
								</div>
							</div>

							<div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
								<Button
									variant="outline"
									onClick={handleClose}
									disabled={isDeleting}
									className="w-full sm:w-auto"
								>
									{t('common.cancel')}
								</Button>
								<Button
									variant="outline"
									className="!border-red-500 !text-red-500 hover:!bg-red-50 dark:hover:!bg-red-900/20 w-full sm:w-auto"
									onClick={handleConfirm}
									isLoading={isDeleting}
									leftIcon={!isDeleting ? <FiTrash2 /> : undefined}
								>
									{isDeleting ? t('menuItems.deleting') : t('common.delete')}
								</Button>
							</div>
						</motion.div>
					</div>
				</div>
			)}
		</AnimatePresence>
	);
};

export default DeleteMenuItemModal;